import * as THREE from "three";
import {
  CAPSULE_HEIGHT_CROUCH,
  CAPSULE_HEIGHT_STAND,
  type AnimState,
  type CollisionWorld,
  type LimbId,
  type Stance,
  type TacticalKind,
  type Vec3,
} from "@rietz/shared";
import {
  GEAR_DARK,
  GEAR_HELMET_COLOR,
  GEAR_MID,
  GEAR_VEST_COLOR,
  SKIN_COLOR,
  TAC_LAMP_COLOR,
  TAC_LASER_COLOR,
  TEAM_COLOR,
  cssHex,
  hashString,
  rand01,
} from "./palette.js";
import {
  HALF_PI,
  deathImpulseFor,
  fallDirLocal,
  kickSpring,
  stepSpring,
  stepTopple,
  type FallStyle,
  type Spring,
} from "./ragdoll.js";
import { SHADOWS_ENABLED } from "./shadows.js";
import { buildWeapon, type BuiltWeapon } from "./weaponBuilder.js";

/**
 * The low-poly humanoid. Constraints in priority order: readability beats
 * fidelity (a bright cap brim on −Z, exaggerated limb swing); the silhouette
 * matches the capsule (1.8 m / 1.3 m crouched, shoulders inside
 * `CAPSULE_RADIUS`); zero per-frame allocation. Animation is procedural and
 * driven by distance travelled, so feet never skate whatever the packet timing.
 *
 * THE TWO-HANDED HOLD: an armed rig does not swing its arms. Hand boxes are
 * parented to the GUN at `BuiltWeapon.grips`, and each arm is aimed at its own
 * hand — the shoulder pivot rotated so local −Y points at it and the limb box
 * scaled to reach it. A one-bone solve; these arms have no elbow. Blended at
 * `BLEND_RATE` like crouch and death.
 */

const STAND_H = CAPSULE_HEIGHT_STAND; // 1.80
const CROUCH_H = CAPSULE_HEIGHT_CROUCH; // 1.30

// Proportions, in metres at standing height.
const LEG_LEN = 0.86;
const TORSO_H = 0.62;
const HEAD_S = 0.24;
const ARM_LEN = 0.6;
const SHOULDER_W = 0.42;
const HIP_W = 0.19;

/** Radians of limb swing per metre travelled — tuned by eye at walk speed. */
const SWING_PER_METRE = 1.65;
/** Swing amplitude by animation state. */
const SWING_AMPLITUDE: Record<AnimState, number> = {
  idle: 0,
  walk: 0.62,
  sprint: 1.05,
  dead: 0,
};

/** How fast stance/death blends resolve, per second. */
const BLEND_RATE = 9;
/**
 * Name tags disappear past this to keep crowds legible. Not applied to
 * squadmates — a friend who vanishes at 26 m is a friend you shoot.
 */
const NAME_TAG_MAX_DIST = 25;

/** How far above the standing head the name tag's CENTRE sits, in metres. */
const TAG_LIFT = 0.26;

/**
 * The squadmate diamond: world-space scale at zero distance plus growth per
 * metre, so it holds roughly one screen size at every range.
 */
const MARKER_SCALE_BASE = 0.1;
const MARKER_SCALE_PER_M = 0.009;

/** The mint, as the two forms the tag and the marker want it in. */
const TEAM_CSS = cssHex(TEAM_COLOR);
/** The default tag ink, for everybody who is not ours. */
const NAME_CSS = "#eef4fa";
/** The bark bubble's ink — warm pale yellow, nobody else's colour. */
const BARK_CSS = "#ffe08a";
/** A shout reads further than a name tag: hearing who called out is the point. */
const BARK_MAX_DIST = 40;

/** A fist, in metres. Chunky enough to read at 40 m, which is the whole brief. */
const HAND_W = 0.075;
const HAND_H = 0.08;
const HAND_D = 0.085;

/** The muzzle-up cant the gun is carried at even with the eyes level. */
const WEAPON_REST_PITCH = 0.06;
/**
 * How much of the look pitch the weapon takes. Not 1 — a rig following ±π/2
 * would fold the rifle against its chest.
 */
const WEAPON_PITCH_GAIN = 0.85;
/** Look pitch is clamped to this before it reaches the gun. */
const WEAPON_PITCH_LIMIT = 1;

/**
 * How far the arm box may stretch or squash to reach its hand — a clamp so a
 * mis-authored grip gives a slightly wrong arm, not a needle.
 */
const ARM_SCALE_MIN = 0.5;
const ARM_SCALE_MAX = 1.35;

/**
 * A remote torch, shorter and dimmer than the local one: there may be eight
 * real lights in a raid. No shadows.
 */
const TAC_LAMP_DISTANCE_M = 25;
const TAC_LAMP_ANGLE_RAD = 0.34;
const TAC_LAMP_PENUMBRA = 0.45;
const TAC_LAMP_INTENSITY = 3;
const TAC_LAMP_DECAY = 1;

/**
 * How far a remote laser is drawn unobstructed — past this the line is a pixel
 * and the raycast is wasted.
 */
const TAC_BEAM_MAX_M = 40;
/** Beam thickness, metres. */
const TAC_BEAM_WIDTH_M = 0.01;
/**
 * Beam opacity, brighter than the local 0.25: for someone else's beam the LINE
 * is the read — it is how you find the man holding it.
 */
const TAC_BEAM_OPACITY = 0.45;

/**
 * Peak upper-body roll at full lean (~10°) and the lateral slide with it. The
 * roll alone buys ~0.14 m of head travel where the hitbox moves 0.26 m; the
 * shift makes up the difference. Only the `upper` group rolls — the legs stay
 * planted, since the capsule does not move.
 */
const LEAN_ROLL = 0.175;
const LEAN_SLIDE_X = 0.12;

/** One magazine change, seconds. Loops for as long as the flag is set. */
const RELOAD_CYCLE_SEC = 1.1;
/** Peak inboard roll and extra muzzle rise while the magazine is being seated. */
const RELOAD_ROLL = 0.25;
const RELOAD_RISE = 0.12;

/**
 * THE FLINCH: one damped spring per axis, kicked by a hit and left to ring
 * down. 4 Hz at zeta 0.45 peaks about a tenth of a second in and is gone
 * inside half a second — a jolt, not a stagger. `FLINCH_GAIN` turns the
 * per-limb amplitudes into that peak (~0.16 rad for a chest hit).
 */
const FLINCH_OMEGA = Math.PI * 8;
const FLINCH_ZETA = 0.45;
const FLINCH_GAIN = 4;
/** Below this a spring counts as still and the whole block is skipped. */
const FLINCH_QUIET = 1e-4;
/** Metres the chest slides per radian of flinch. */
const FLINCH_SLIDE_M = 0.25;
/** Metres the body dips per unit of `dip` — a hit in the legs buckles. */
const DIP_M = 1.3;

/** Flinch kick per limb hit, rad/s before `FLINCH_GAIN`. */
const FLINCH_AMP: Record<LimbId, number> = {
  head: 0.9,
  thorax: 1.6,
  stomach: 1.4,
  armL: 1,
  armR: 1,
  legL: 0.8,
  legR: 0.8,
};

/** Extra head snap: a head hit whips it, anything else nods it. */
const HEAD_FLINCH_HIT = 2.5;
const HEAD_FLINCH_BODY = 0.6;
/** The struck limb's own jerk, and the dip a leg or gut hit adds. */
const LIMB_FLINCH = 1.5;
const DIP_LEG = 0.6;
const DIP_BODY = 0.25;

/**
 * THE FALL: limbs go floppy at 1.6 Hz, well under the flinch — a corpse's arm
 * swings, it does not twitch.
 */
const LIMP_OMEGA = Math.PI * 3.2;
const LIMP_ZETA = 0.35;
const LIMP_QUIET = 1e-3;
/** A limb is stiff for exactly as long as the fall takes; then it slaps down. */
const LIMP_SLAP_MIN = 0.6;
const LIMP_SLAP_SPAN = 0.6;

/**
 * How far the root rises once the body is flat, so the torso lies ON the
 * ground and not half inside it — plus what a backpack adds under a man who
 * fell onto his back.
 */
export const LIE_LIFT = 0.17;
export const PACK_LIFT = 0.08;
/** ...and what a fall onto the SIDE adds: shoulders are wider than a chest. */
const SIDE_LIFT = 0.09;

/** How far a buckle folds the legs and sinks the chest, on top of a crouch. */
const BUCKLE_FOLD = 0.4;
const BUCKLE_SINK = 0.3;
/** The legs never fold past this — a rig is a silhouette, not a puddle. */
const FOLD_MIN = 0.3;

/** A death primed longer ago than this is stale: the entity died of something
 *  else, or the anim never arrived. */
const PRIME_MAX_SEC = 1.5;

/**
 * What a character visibly wears — structurally `EntitySnap.gear`, declared
 * here so this module knows no wire format.
 */
export interface CharacterGear {
  /** Item def id of the weapon in their hands, or `null` for empty-handed. */
  weapon: string | null;
  vest: boolean;
  helmet: boolean;
  backpack: boolean;
}

export interface CharacterRig {
  readonly group: THREE.Group;
  /** Places the rig for one frame. `pos` is the FEET position. */
  setPose(
    pos: Vec3,
    yaw: number,
    pitch: number,
    stance: Stance,
    anim: AnimState,
    dtMs: number,
  ): void;
  /** Body colour, normally derived from the entity id. */
  setTint(hex: number): void;
  /**
   * Repaints the two secondary tones (trousers/belt/pack and the cap brim) —
   * what turns a green torso into a poacher's silhouette.
   */
  setGearTones(gear: number, accent: number): void;
  /**
   * Applies visible equipment. `null` = no information (default silhouette),
   * NOT a stripped PMC.
   */
  setGear(gear: CharacterGear | null): void;
  /**
   * Whether this character is changing a magazine — a LEVEL off
   * `EntitySnap.reload`; the rig loops while set. Free to call every frame;
   * ignored empty-handed.
   */
  setReloading(reloading: boolean): void;
  /**
   * Peeking: −1 left, +1 right, 0 upright. A level off `EntitySnap.lean`.
   * Not decoration: the server moves the hitboxes by the same mechanic, so a
   * rig that did not tilt would be a silhouette you cannot shoot where you see it.
   */
  setLean(lean: -1 | 0 | 1): void;
  /**
   * Which mounted device is switched on, or `null`. A level like the two above,
   * but the one that can REBUILD THE GUN, so change-guarded internally. Remote
   * guns are drawn without anybody's parts (the wire carries a def id only);
   * a lit device is the exception, since the beam announces itself and a red
   * line from a bare rifle would read as a bug.
   */
  setTactical(tac: TacticalKind | null): void;
  /**
   * Clamps a lit laser to the first thing in front of the muzzle. Separate from
   * `setTactical` because it is a per-frame world query and only the caller
   * knows the current collision world. `null` falls back to full length.
   * A no-op unless a laser is lit.
   */
  updateTactical(world: CollisionWorld | null): void;
  /**
   * Sets (or clears) the name tag and whether it is one of ours. `team` is
   * part of the tag's identity (the ink is baked into the canvas) and raises
   * the diamond. `accent` is an optional CSS ink for role-coloured lettering
   * (`roleTagInkFor`) and loses to `team`. Idempotent and change-guarded.
   */
  setName(name: string, team?: boolean, accent?: string): void;
  /**
   * Shows a speech line above the name tag, or clears it with `null`.
   * Change-guarded — the text is baked into a canvas.
   */
  setBark(text: string | null): void;
  /**
   * A hit that did NOT kill: the body jolts away from `from` (the shooter's
   * world position, or `null` for unknown — then backward) and the struck limb
   * jerks. Ignored by a dying or dead rig.
   */
  hit(limb: LimbId, from: Vec3 | null): void;
  /**
   * Primes the death fall with what did it, so the ragdoll knows which way to
   * go before the interpolated `dead` anim catches up (~100 ms). `limb` `null`
   * is a bleed-out or a starvation: it sags where it stands.
   */
  kill(from: Vec3 | null, limb: LimbId | null): void;
  /** Sizes the name tag, the squad marker and the bark bubble by camera
   *  distance, and hides what is out of range. Cheap; call once per frame. */
  updateNameTag(cameraPos: THREE.Vector3): void;
  dispose(): void;
}

// --- Module scratch: never allocate inside setPose --------------------------
const tmpVec = new THREE.Vector3();
const tmpQuat = new THREE.Quaternion();
const tmpQuatGait = new THREE.Quaternion();
const tmpEuler = new THREE.Euler();
/** Scratch for the laser's world-space clamp — see `updateTactical`. */
const tmpMuzzlePos = new THREE.Vector3();
const tmpMuzzleDir = new THREE.Vector3();
const tmpRayFrom: Vec3 = { x: 0, y: 0, z: 0 };
const tmpRayDir: Vec3 = { x: 0, y: 0, z: 0 };
/** The axis a limb box hangs along, which is what the solve rotates ONTO the
 *  shoulder→hand direction. Constant; never written to. */
const LIMB_AXIS = new THREE.Vector3(0, -1, 0);
/** The axis a struck arm jerks about. Constant; never written to. */
const AXIS_X = new THREE.Vector3(1, 0, 0);
/** Scratch for a push direction in the rig's own frame — see `fallDirLocal`. */
const tmpDir = { x: 0, z: 0 };

/**
 * Every rig shares the same handful of box sizes — geometries are shared
 * process-wide; only materials and the tag texture are per rig.
 */
const geoCache = new Map<string, THREE.BoxGeometry>();

function boxGeo(w: number, h: number, d: number): THREE.BoxGeometry {
  const key = `${w}|${h}|${d}`;
  let g = geoCache.get(key);
  if (g === undefined) {
    g = new THREE.BoxGeometry(w, h, d);
    geoCache.set(key, g);
  }
  return g;
}

interface Limb {
  /** Pivot at the joint; rotate this. */
  pivot: THREE.Group;
  mesh: THREE.Mesh;
}

/**
 * A limp spring carries the pose it settles into, so stepping needs no table.
 * `mag` is the pose as authored for this rig; `rest` is that pose re-signed
 * for the fall it is in, so no joint bends into the ground — see `aimLimp`.
 */
interface Limp extends Spring {
  readonly mag: number;
  rest: number;
}

function spring(): Spring {
  return { x: 0, v: 0 };
}

function limp(mag: number): Limp {
  return { x: 0, v: 0, mag, rest: mag };
}

/** Bends this joint toward `s` (±1), or back to the authored pose at 0. */
function aimLimp(l: Limp, s: number): void {
  l.rest = s === 0 ? l.mag : Math.abs(l.mag) * s;
}

/** ±1, deterministic per rig. */
function sign01(seed: number, salt: number): number {
  return rand01(seed, salt) < 0.5 ? -1 : 1;
}

class Rig implements CharacterRig {
  readonly group = new THREE.Group();

  /** Everything below the feet-anchored root, so crouch can sink the body. */
  private readonly body = new THREE.Group();
  private readonly hips = new THREE.Group();
  /**
   * Everything above the waist, so a lean can tilt it without the legs. Sits
   * at the hips' origin (`LEG_LEN` off the ground — the spine base). The arms
   * and the weapon anchor hang off it too, which `poseArm`'s solve depends on.
   */
  private readonly upper = new THREE.Group();
  private readonly torso: THREE.Mesh;
  private readonly headPivot = new THREE.Group();
  private readonly legL: Limb;
  private readonly legR: Limb;
  private readonly armL: Limb;
  private readonly armR: Limb;

  /** Gear overlays: built once, shown or hidden by `setGear`. */
  private readonly pack: THREE.Mesh;
  private readonly vest: THREE.Mesh;
  private readonly helmet: THREE.Mesh;
  private readonly weaponAnchor = new THREE.Group();
  private weaponModel: BuiltWeapon | null = null;
  /** Def id of the built weapon model, or `null` when empty-handed. */
  private weaponModelId: string | null = null;
  /**
   * The gun and device the caller asked for; `weaponModelId`/`builtTac` are
   * what the model was built from. The rebuild key is the pair.
   */
  private wantWeaponId: string | null = null;
  private wantTac: TacticalKind | null = null;
  /** The device the current model was built showing, or `null`. */
  private builtTac: TacticalKind | null = null;
  /**
   * The remote torch and beam, parented under the built weapon's muzzle so they
   * follow the aim for free. Created lazily on the first device switched on.
   */
  private tacLight: THREE.SpotLight | null = null;
  private tacLightTarget: THREE.Object3D | null = null;
  private tacBeam: THREE.Mesh | null = null;
  private tacBeamGeo: THREE.BoxGeometry | null = null;
  private tacBeamMat: THREE.MeshBasicMaterial | null = null;
  /**
   * The two fists, parented to the WEAPON rather than the arms — a hand welded
   * to the gun is on it in every frame. `null` while empty-handed.
   */
  private handTrigger: THREE.Mesh | null = null;
  private handSupport: THREE.Mesh | null = null;
  /** Rest points for the two hands, and the magazine the support hand fetches,
   *  all in the WEAPON's local space. Meaningless while empty-handed. */
  private readonly gripTrigger = new THREE.Vector3();
  private readonly gripSupport = new THREE.Vector3();
  private readonly gripMag = new THREE.Vector3();

  private readonly bodyMat: THREE.MeshLambertMaterial;
  private readonly gearMat: THREE.MeshLambertMaterial;
  private readonly skinMat: THREE.MeshLambertMaterial;
  private readonly accentMat: THREE.MeshLambertMaterial;
  private readonly vestMat: THREE.MeshLambertMaterial;
  private readonly helmetMat: THREE.MeshLambertMaterial;

  private tag: THREE.Sprite | null = null;
  private tagTexture: THREE.CanvasTexture | null = null;
  private tagName = "";
  /** Whether the tag currently on the head was drawn as a squadmate's. Part of
   *  the rebuild key — see `setName`. */
  private tagTeam = false;
  /**
   * The role ink the tag was lettered in — the third part of the rebuild key.
   */
  private tagAccent: string | undefined = undefined;
  /** The squadmate diamond, or `null` for everyone who is not one. */
  private marker: THREE.Sprite | null = null;

  /** The shout above the tag, and the text it was drawn from — the rebuild
   *  key. `null` while he is saying nothing. */
  private bark: THREE.Sprite | null = null;
  private barkTexture: THREE.CanvasTexture | null = null;
  private barkText: string | null = null;

  /** Accumulated gait phase, radians. */
  private phase = 0;
  /** Accumulated time for the idle breathing bob, seconds. */
  private clock = 0;
  /** 0 = standing, 1 = fully crouched. Blended, so stance changes are smooth. */
  private crouch = 0;
  /** 0 = upright, 1 = collapsed. */
  private death = 0;
  /** 0 = arms swinging free, 1 = both hands on the gun. */
  private hold = 0;
  /** What the caller last said; the blend below is what is drawn. */
  private reloadWanted = false;
  /** 0 = normal hold, 1 = fully into the magazine change. */
  private reload = 0;
  /** What the caller last said the peek is; `leanBlend` is what is drawn. */
  private leanWanted: -1 | 0 | 1 = 0;
  /** -1…+1, eased toward `leanWanted`. */
  private leanBlend = 0;
  /** Seconds into the looping reload cycle. Reset when the blend dies. */
  private reloadPhase = 0;
  /** Blended look pitch, so the muzzle sweeps rather than snapping. */
  private aimPitch = 0;
  /** Last position, for the distance-driven gait. `null` until first pose. */
  private lastX = 0;
  private lastZ = 0;
  private hasLast = false;

  // --- Hit reactions. All springs live for the rig's life. ------------------
  /** Upper-body tilt about local X and Z, and the slide that goes with it. */
  private readonly flinchX = spring();
  private readonly flinchZ = spring();
  private readonly headFlinchX = spring();
  private readonly headFlinchZ = spring();
  /** The struck limb's own jerk. */
  private readonly armFlinchL = spring();
  private readonly armFlinchR = spring();
  private readonly legFlinchL = spring();
  private readonly legFlinchR = spring();
  /** A knee-buckling dip, in `DIP_M` metres. */
  private readonly dip = spring();
  /** The nine above, so one loop steps and settles them. */
  private readonly flinches: readonly Spring[];
  /** Set by any kick, cleared when every flinch spring is still again. */
  private flinchLive = false;

  // --- The fall ------------------------------------------------------------
  private readonly fall = {
    active: false,
    angle: 0,
    angVel: 0,
    bounced: false,
    settled: false,
  };
  /** Horizontal axis the body topples about, in its own frame. */
  private readonly fallAxis = new THREE.Vector3(1, 0, 0);
  /** How far onto his back he is going; +1 is flat on the pack. */
  private fallDirZ = 1;
  /** ...and how far onto his side; the lift needs both. */
  private fallDirX = 0;
  private landed = false;
  /** A settled corpse costs one transform a frame — see `setPose`. */
  private frozen = false;
  /** Metres the root is lifted this frame, kept for the frozen path. */
  private lieLift = 0;
  /** 0 = legs under him, 1 = folded. Blended like crouch. */
  private buckle = 0;
  private buckleTarget = 0;

  /** The floppy pose, one spring per joint; `rest` is where it settles. */
  private readonly limpLegXL: Limp;
  private readonly limpLegXR: Limp;
  private readonly limpLegZL: Limp;
  private readonly limpLegZR: Limp;
  private readonly limpArmXL: Limp;
  private readonly limpArmXR: Limp;
  private readonly limpArmZL: Limp;
  private readonly limpArmZR: Limp;
  private readonly limpHeadX: Limp;
  private readonly limpHeadZ: Limp;
  /** The ten above, for stepping and settling tests. */
  private readonly limps: readonly Limp[];
  /** Drives every per-rig random above and the slap on landing. */
  private readonly seed: number;

  /** What killed him, recorded before the `dead` anim arrives. */
  private hasPrime = false;
  private primeAt = 0;
  private primeDirX = 0;
  private primeDirZ = 1;
  private primeFrom = false;
  private primeStyle: FallStyle = "topple";
  private primeAngVel = 2.3;

  constructor(seed = 0) {
    this.group.name = "character";

    // The limp pose is drawn once, per rig: a field of corpses that all lie the
    // same way reads as a bug.
    const kneeL = rand01(seed, 25) < 0.5;
    // The four bends are authored as MAGNITUDES: which way they go is the fall
    // direction's business, so a knee never bends into the ground — see
    // `startFall`.
    this.limpLegXL = limp(0.08 + rand01(seed, 22) * 0.2);
    this.limpLegXR = limp(0.08 + rand01(seed, 24) * 0.2);
    // Splay is outward: about +Z the foot swings toward +x, so the left leg's
    // is negative.
    this.limpLegZL = limp(-(kneeL ? 0.2 + rand01(seed, 26) * 0.15 : 0.04));
    this.limpLegZR = limp(kneeL ? 0.04 : 0.2 + rand01(seed, 27) * 0.15);
    this.limpArmXL = limp(0.1 + rand01(seed, 29) * 0.25);
    this.limpArmXR = limp(0.1 + rand01(seed, 31) * 0.25);
    this.limpArmZL = limp(sign01(seed, 32) * (0.5 + rand01(seed, 33) * 0.4));
    this.limpArmZR = limp(sign01(seed, 34) * (0.5 + rand01(seed, 35) * 0.4));
    this.limpHeadX = limp(0.1 + rand01(seed, 37) * 0.15);
    this.limpHeadZ = limp(sign01(seed, 38) * (0.25 + rand01(seed, 39) * 0.2));
    this.limps = [
      this.limpLegXL,
      this.limpLegXR,
      this.limpLegZL,
      this.limpLegZR,
      this.limpArmXL,
      this.limpArmXR,
      this.limpArmZL,
      this.limpArmZR,
      this.limpHeadX,
      this.limpHeadZ,
    ];
    this.flinches = [
      this.flinchX,
      this.flinchZ,
      this.headFlinchX,
      this.headFlinchZ,
      this.armFlinchL,
      this.armFlinchR,
      this.legFlinchL,
      this.legFlinchR,
      this.dip,
    ];
    this.seed = seed;

    this.bodyMat = new THREE.MeshLambertMaterial({
      color: 0xffffff,
      flatShading: true,
    });
    this.gearMat = new THREE.MeshLambertMaterial({
      color: GEAR_DARK,
      flatShading: true,
    });
    this.skinMat = new THREE.MeshLambertMaterial({
      color: SKIN_COLOR,
      flatShading: true,
    });
    this.accentMat = new THREE.MeshLambertMaterial({
      color: GEAR_MID,
      flatShading: true,
    });
    this.vestMat = new THREE.MeshLambertMaterial({
      color: GEAR_VEST_COLOR,
      flatShading: true,
    });
    this.helmetMat = new THREE.MeshLambertMaterial({
      color: GEAR_HELMET_COLOR,
      flatShading: true,
    });

    this.group.add(this.body);
    this.body.add(this.hips);
    this.hips.position.y = LEG_LEN;
    this.hips.add(this.upper);
    // Named so tests (and scene dumps) can find the joints the fall moves.
    this.body.name = "body";
    this.hips.name = "hips";
    this.upper.name = "upper";
    this.headPivot.name = "head";

    // --- Torso + backpack --------------------------------------------------
    this.torso = new THREE.Mesh(boxGeo(0.46, TORSO_H, 0.26), this.bodyMat);
    this.torso.position.y = TORSO_H * 0.5;
    this.upper.add(this.torso);

    const pack = new THREE.Mesh(boxGeo(0.34, 0.4, 0.16), this.gearMat);
    // Yaw 0 faces −Z, so the pack rides on +Z.
    pack.position.set(0, TORSO_H * 0.55, 0.2);
    this.upper.add(pack);
    this.pack = pack;

    // The plate carrier: a larger shell around the chest, bulk added ON TOP of
    // the body.
    this.vest = new THREE.Mesh(boxGeo(0.5, TORSO_H * 0.72, 0.32), this.vestMat);
    this.vest.position.y = TORSO_H * 0.52;
    this.vest.visible = false;
    this.upper.add(this.vest);

    const belt = new THREE.Mesh(boxGeo(0.48, 0.1, 0.28), this.gearMat);
    belt.position.y = 0.04;
    this.hips.add(belt);

    // Carried at the chest, pointing −Z. Parented to the hips, not an arm: THE
    // GUN LEADS AND THE ARMS FOLLOW, so the arms can never pull it out of line.
    this.weaponAnchor.position.set(0.14, TORSO_H * 0.58, -0.16);
    this.weaponAnchor.rotation.x = WEAPON_REST_PITCH;
    this.upper.add(this.weaponAnchor);

    // --- Head --------------------------------------------------------------
    this.headPivot.position.y = TORSO_H + 0.06;
    this.upper.add(this.headPivot);

    const neck = new THREE.Mesh(boxGeo(0.12, 0.09, 0.12), this.skinMat);
    neck.position.y = -0.045;
    this.headPivot.add(neck);

    const head = new THREE.Mesh(boxGeo(HEAD_S, HEAD_S, HEAD_S), this.skinMat);
    head.position.y = HEAD_S * 0.5;
    this.headPivot.add(head);

    const cap = new THREE.Mesh(
      boxGeo(HEAD_S + 0.02, 0.07, HEAD_S + 0.02),
      this.bodyMat,
    );
    cap.position.y = HEAD_S - 0.005;
    this.headPivot.add(cap);

    // The brim is the whole point: a symmetric head gives no facing cue, and
    // without a facing cue you cannot eyeball a rotation or lag bug.
    const brim = new THREE.Mesh(
      boxGeo(HEAD_S + 0.02, 0.035, 0.1),
      this.accentMat,
    );
    brim.position.set(0, HEAD_S - 0.02, -(HEAD_S * 0.5 + 0.05));
    this.headPivot.add(brim);

    // The helmet: a shell over the cap, tall enough to change the silhouette at
    // distance.
    this.helmet = new THREE.Mesh(
      boxGeo(HEAD_S + 0.07, HEAD_S * 0.78, HEAD_S + 0.07),
      this.helmetMat,
    );
    this.helmet.position.y = HEAD_S * 0.66;
    this.helmet.visible = false;
    this.headPivot.add(this.helmet);

    // --- Limbs -------------------------------------------------------------
    this.legL = this.makeLimb(-HIP_W, 0, LEG_LEN, 0.16, this.gearMat, this.hips);
    this.legR = this.makeLimb(HIP_W, 0, LEG_LEN, 0.16, this.gearMat, this.hips);

    const shoulderY = TORSO_H - 0.08;
    this.armL = this.makeLimb(
      -SHOULDER_W * 0.5 - 0.06,
      shoulderY,
      ARM_LEN,
      0.13,
      this.bodyMat,
      this.upper,
    );
    this.armR = this.makeLimb(
      SHOULDER_W * 0.5 + 0.06,
      shoulderY,
      ARM_LEN,
      0.13,
      this.bodyMat,
      this.upper,
    );
    this.legL.pivot.name = "legL";
    this.legR.pivot.name = "legR";
    this.armL.pivot.name = "armL";
    this.armR.pivot.name = "armR";

    // `?shadows=1` only. A traversal, since three does not inherit these flags;
    // the weapon is marked separately in `setWeaponModel`.
    markShadowCasters(this.group);
  }

  private makeLimb(
    x: number,
    y: number,
    length: number,
    width: number,
    mat: THREE.MeshLambertMaterial,
    parent: THREE.Object3D,
  ): Limb {
    const pivot = new THREE.Group();
    pivot.position.set(x, y, 0);
    parent.add(pivot);

    const mesh = new THREE.Mesh(boxGeo(width, length, width), mat);
    // Hangs down from the joint, so rotating the pivot swings the limb.
    mesh.position.y = -length * 0.5;
    pivot.add(mesh);

    return { pivot, mesh };
  }

  setTint(hex: number): void {
    this.bodyMat.color.setHex(hex);
  }

  setGearTones(gear: number, accent: number): void {
    this.gearMat.color.setHex(gear);
    this.accentMat.color.setHex(accent);
  }

  setGear(gear: CharacterGear | null): void {
    if (gear === null) {
      // No information: the default look. Not "everything off".
      this.pack.visible = true;
      this.vest.visible = false;
      this.helmet.visible = false;
      this.wantWeaponId = null;
      this.syncWeaponModel();
      return;
    }
    this.pack.visible = gear.backpack;
    this.vest.visible = gear.vest;
    this.helmet.visible = gear.helmet;
    this.wantWeaponId = gear.weapon;
    this.syncWeaponModel();
  }

  setReloading(reloading: boolean): void {
    this.reloadWanted = reloading;
  }

  setLean(lean: -1 | 0 | 1): void {
    this.leanWanted = lean;
  }

  setTactical(tac: TacticalKind | null): void {
    if (tac === this.wantTac) return;
    this.wantTac = tac;
    this.syncWeaponModel();
  }

  updateTactical(world: CollisionWorld | null): void {
    const beam = this.tacBeam;
    if (beam === null || !beam.visible) return;
    const muzzle = this.weaponModel?.muzzle;
    if (muzzle === undefined) return;

    // One matrix update instead of two world lookups: translation is columns
    // 12..14, the gun's forward is the negated third basis vector. The world
    // matrices are stale until asked.
    muzzle.updateWorldMatrix(true, false);
    const m = muzzle.matrixWorld.elements;
    tmpMuzzlePos.set(m[12] as number, m[13] as number, m[14] as number);
    tmpMuzzleDir.set(-(m[8] as number), -(m[9] as number), -(m[10] as number)).normalize();

    let len = TAC_BEAM_MAX_M;
    if (world !== null) {
      tmpRayFrom.x = tmpMuzzlePos.x;
      tmpRayFrom.y = tmpMuzzlePos.y;
      tmpRayFrom.z = tmpMuzzlePos.z;
      tmpRayDir.x = tmpMuzzleDir.x;
      tmpRayDir.y = tmpMuzzleDir.y;
      tmpRayDir.z = tmpMuzzleDir.z;
      const hit = world.raycast(tmpRayFrom, tmpRayDir, TAC_BEAM_MAX_M);
      if (hit !== null) len = hit.dist;
    }
    // The box is one metre along −Z off the muzzle: length is `scale.z`, centre
    // half of it.
    beam.scale.set(1, 1, len);
    beam.position.z = -len * 0.5;
  }

  /**
   * Builds, rebuilds or tears down the gun. Keyed on the (gun, lit device)
   * pair; a snapshot repeats itself 15 Hz, so the guard matters.
   */
  private syncWeaponModel(): void {
    const id = this.wantWeaponId;
    const tac = this.wantTac;
    if (id === this.weaponModelId && tac === this.builtTac) return;
    this.weaponModelId = id;
    this.builtTac = tac;

    const old = this.weaponModel;
    if (old !== null) {
      // The light and beam are the rig's: detached so `dispose` cannot take
      // them.
      this.tacLight?.removeFromParent();
      this.tacLightTarget?.removeFromParent();
      this.tacBeam?.removeFromParent();
      // The hands go with the gun's group; their geometry is shared and their
      // material the rig's.
      this.weaponAnchor.remove(old.group);
      old.dispose();
      this.weaponModel = null;
      this.handTrigger = null;
      this.handSupport = null;
    }
    if (id === null) {
      // Nothing to hang a light off. The device stays wanted, but nothing is
      // lit.
      this.applyTacticalVisibility();
      return;
    }

    // No mod list (the wire carries a def id only), so the default `"factory"`
    // view: an empty slot means "not told", not "off". The one exception is a
    // lit device, which is not hidden information — the lump appears and
    // disappears with the light.
    const built = buildWeapon(
      id,
      tac === null ? [] : [tac === "lamp" ? "flashlight" : "laser"],
    );
    this.gripTrigger.copy(built.grips.trigger);
    this.gripSupport.copy(built.grips.support);
    // No gun in the roster lacks a mag anchor, but the map is partial; the
    // origin would be the shooter's own eye.
    this.gripMag.copy(built.modAnchors.mag ?? built.grips.support);

    this.handTrigger = this.makeHand(built.group, this.gripTrigger);
    this.handSupport = this.makeHand(built.group, this.gripSupport);

    this.weaponAnchor.add(built.group);
    // After the hands, so they are marked with the rest of the gun.
    markShadowCasters(built.group);
    this.weaponModel = built;

    // Last, because it needs the new muzzle. Both effects hang UNDER it — see
    // the fields — so from here on they follow the aim with no work per frame.
    if (tac !== null) this.attachTactical(built.muzzle, tac);
    this.applyTacticalVisibility();
  }

  /**
   * Puts the torch and beam on `muzzle`, building them the first time. The
   * spot's target rides under the muzzle 20 m down −Z, so the cone follows the
   * aim.
   */
  private attachTactical(muzzle: THREE.Object3D, tac: TacticalKind): void {
    // Built per kind; whichever exists is kept for the rig's life.
    if (tac === "lamp" && this.tacLight === null) {
      const light = new THREE.SpotLight(
        TAC_LAMP_COLOR,
        TAC_LAMP_INTENSITY,
        TAC_LAMP_DISTANCE_M,
        TAC_LAMP_ANGLE_RAD,
        TAC_LAMP_PENUMBRA,
        TAC_LAMP_DECAY,
      );
      light.castShadow = false;
      const target = new THREE.Object3D();
      target.position.set(0, 0, -20);
      light.target = target;
      this.tacLight = light;
      this.tacLightTarget = target;
    }
    if (tac === "laser" && this.tacBeam === null) {
      // A unit metre along −Z, stretched by `updateTactical` — the same recipe
      // the tracer pool uses, and for the same reason: one geometry, one scale.
      this.tacBeamGeo = new THREE.BoxGeometry(TAC_BEAM_WIDTH_M, TAC_BEAM_WIDTH_M, 1);
      this.tacBeamMat = new THREE.MeshBasicMaterial({
        color: TAC_LASER_COLOR,
        blending: THREE.AdditiveBlending,
        transparent: true,
        opacity: TAC_BEAM_OPACITY,
        depthWrite: false,
        // Unlit and unfogged, exactly like a tracer: the beam is its own light,
        // and haze that swallowed it would swallow it at the range that matters.
        fog: false,
      });
      const beam = new THREE.Mesh(this.tacBeamGeo, this.tacBeamMat);
      // It is stretched across tens of metres from a bone deep in the rig; a
      // bounding sphere from the unit box would be permanently wrong.
      beam.frustumCulled = false;
      beam.renderOrder = 5;
      this.tacBeam = beam;
    }
    // Attached AFTER `markShadowCasters` ran over the gun (see the caller), so
    // neither is marked — a beam is light, not an object standing in light.
    if (tac === "lamp") {
      if (this.tacLight !== null) muzzle.add(this.tacLight);
      if (this.tacLightTarget !== null) muzzle.add(this.tacLightTarget);
    } else if (this.tacBeam !== null) {
      muzzle.add(this.tacBeam);
    }
  }

  /** Whichever of the two the wire currently says is on — and neither, once the
   *  gun is out of the hands. Cheap, and idempotent. */
  private applyTacticalVisibility(): void {
    const armed = this.weaponModel !== null;
    if (this.tacLight !== null) this.tacLight.visible = armed && this.builtTac === "lamp";
    if (this.tacBeam !== null) this.tacBeam.visible = armed && this.builtTac === "laser";
  }

  /** One fist, welded to the gun at a point the model chose. Painted from the
   *  rig's own gear tone, so a Wilderer's gloves are brown and a PMC's grey. */
  private makeHand(parent: THREE.Object3D, at: THREE.Vector3): THREE.Mesh {
    const mesh = new THREE.Mesh(boxGeo(HAND_W, HAND_H, HAND_D), this.gearMat);
    mesh.position.copy(at);
    parent.add(mesh);
    return mesh;
  }

  setName(name: string, team = false, accent?: string): void {
    // All three are the key: the ink is baked into the canvas.
    if (name === this.tagName && team === this.tagTeam && accent === this.tagAccent) {
      return;
    }
    this.tagName = name;
    this.tagTeam = team;
    this.tagAccent = accent;
    this.disposeTag();
    // The diamond is torn down with the tag rather than kept hidden — it exists
    // for a handful of entities.
    this.disposeMarker();
    if (name.length === 0) return;

    const made = makeNameSprite(name, team, accent);
    if (made === null) return;
    // Just above the standing head.
    made.sprite.position.y = STAND_H + TAG_LIFT;
    this.tag = made.sprite;
    this.tagTexture = made.texture;
    this.group.add(made.sprite);

    if (!team) return;
    this.marker = makeTeamMarker();
    this.group.add(this.marker);
  }

  setBark(text: string | null): void {
    if (text === this.barkText) return;
    this.barkText = text;
    this.disposeBark();
    if (text === null || text.length === 0) return;

    const made = makeTextSprite(`„${text}“`, BARK_CSS);
    if (made === null) return;
    this.bark = made.sprite;
    this.barkTexture = made.texture;
    this.group.add(made.sprite);
    // Placed by `updateNameTag` — the only thing that knows the range.
  }

  updateNameTag(cameraPos: THREE.Vector3): void {
    const tag = this.tag;
    const marker = this.marker;
    const bark = this.bark;
    if (tag === null && marker === null && bark === null) return;
    this.group.getWorldPosition(tmpVec);
    const dist = tmpVec.distanceTo(cameraPos);
    // A corpse wears no tag and no diamond — a green marker over one reads as
    // "he is fine".
    const alive = this.death < 0.5;
    // Hold a roughly constant on-screen size at any range.
    const s = 0.5 + dist * 0.012;

    if (tag !== null) {
      // The range cut applies to STRANGERS only — see `NAME_TAG_MAX_DIST`.
      tag.visible = alive && (this.tagTeam || dist <= NAME_TAG_MAX_DIST);
      if (tag.visible) tag.scale.set(s * tag.userData.aspect, s, 1);
    }

    if (marker !== null) {
      marker.visible = alive;
      if (marker.visible) {
        const m = MARKER_SCALE_BASE + dist * MARKER_SCALE_PER_M;
        marker.scale.set(m, m, 1);
        // Stacked on top of the tag; both grow with range, so a constant offset
        // would sink it.
        marker.position.y = STAND_H + TAG_LIFT + s * 0.5 + m * 0.8;
      }
    }

    if (bark !== null) {
      bark.visible = alive && dist <= BARK_MAX_DIST;
      if (bark.visible) {
        bark.scale.set(s * bark.userData.aspect, s, 1);
        // Above the tag, lifted by the tag's own scale so the two do not
        // overlap at range.
        bark.position.y = STAND_H + TAG_LIFT + s * 0.5 + s * 0.6;
      }
    }
  }

  setPose(
    pos: Vec3,
    yaw: number,
    pitch: number,
    stance: Stance,
    anim: AnimState,
    dtMs: number,
  ): void {
    const dt = Math.min(Math.max(dtMs, 0), 100) / 1000;

    // A corpse that has finished falling is one transform a frame: every blend
    // below has already resolved. Only a respawn (`anim !== "dead"`) wakes it.
    if (this.frozen && anim === "dead") {
      this.group.position.set(pos.x, pos.y + this.lieLift, pos.z);
      this.group.rotation.y = yaw;
      this.lastX = pos.x;
      this.lastZ = pos.z;
      return;
    }
    this.clock += dt;

    // Seen dead the first time we ever saw him — a late joiner, or a body that
    // fell out of interest range. There was no fall to watch, so start flat.
    if (!this.hasLast && anim === "dead") this.snapToCorpse();

    // --- Root ---------------------------------------------------------------
    this.group.position.set(pos.x, pos.y, pos.z);
    this.group.rotation.y = yaw;

    // --- Gait phase from distance travelled ---------------------------------
    let moved = 0;
    if (this.hasLast) {
      const dx = pos.x - this.lastX;
      const dz = pos.z - this.lastZ;
      moved = Math.sqrt(dx * dx + dz * dz);
      // A teleport (respawn, big correction) must not spin the legs.
      if (moved > 2) moved = 0;
    }
    this.lastX = pos.x;
    this.lastZ = pos.z;
    this.hasLast = true;

    const amp = SWING_AMPLITUDE[anim];
    this.phase += moved * SWING_PER_METRE;
    if (this.phase > Math.PI * 2) this.phase -= Math.PI * 2;

    // --- Blends -------------------------------------------------------------
    const k = Math.min(1, dt * BLEND_RATE);
    this.crouch += ((stance === "crouch" ? 1 : 0) - this.crouch) * k;
    this.death += ((anim === "dead" ? 1 : 0) - this.death) * k;
    // A dead rig never leans — folded into the target so it unwinds through the
    // blend.
    this.leanBlend += ((anim === "dead" ? 0 : this.leanWanted) - this.leanBlend) * k;

    // --- The fall -----------------------------------------------------------
    // It starts on the ANIM, not on `kill`: the interpolator is ~100 ms behind,
    // and a fall begun early would be reset by the next idle pose.
    if (anim === "dead") {
      if (!this.fall.active) this.startFall();
      this.stepFall(dt);
    } else if (this.fall.active) {
      this.endFall();
    }
    const falling = this.fall.active;
    this.buckle += (this.buckleTarget - this.buckle) * k;
    if (this.flinchLive) this.stepFlinch(dt);

    // --- Stance: crouching sinks the hips and folds the legs to the crouched
    // capsule height; a buckle folds them further.
    const fold = 1 - 0.34 * this.crouch - BUCKLE_FOLD * this.buckle;
    const legFold = clamp(fold, FOLD_MIN, 1);
    this.hips.position.y = LEG_LEN * legFold;
    this.legL.mesh.scale.y = legFold;
    this.legR.mesh.scale.y = legFold;
    this.legL.mesh.position.y = -LEG_LEN * 0.5 * legFold;
    this.legR.mesh.position.y = -LEG_LEN * 0.5 * legFold;

    const bob =
      amp === 0
        ? Math.sin(this.clock * 1.9) * 0.012
        : Math.abs(Math.sin(this.phase)) * 0.035 * amp;
    this.body.position.y =
      bob -
      (STAND_H - CROUCH_H) * this.crouch * 0.36 -
      BUCKLE_SINK * this.buckle -
      this.dip.x * DIP_M;
    // Crouched and sprinting both lean the chest forward.
    this.hips.rotation.x =
      this.crouch * 0.22 + (anim === "sprint" ? 0.16 : 0) * (1 - this.death);

    // --- The peek, plus the flinch: both tilt and slide the upper body about
    // the spine base. NEGATIVE roll for a lean to the RIGHT (rotation about +Z
    // takes +y to x = −y·sinθ). Written every frame — cheaper than the branch.
    this.upper.rotation.set(
      this.flinchX.x,
      0,
      -this.leanBlend * LEAN_ROLL + this.flinchZ.x,
    );
    this.upper.position.set(
      this.leanBlend * LEAN_SLIDE_X - this.flinchZ.x * FLINCH_SLIDE_M,
      0,
      this.flinchX.x * FLINCH_SLIDE_M,
    );

    // --- Limb swing ---------------------------------------------------------
    const swing = Math.sin(this.phase) * amp;
    const counter = Math.sin(this.phase + Math.PI) * amp;
    if (falling) {
      // The gait is over; the legs hang where the springs put them.
      this.legL.pivot.rotation.set(this.limpLegXL.x, 0, this.limpLegZL.x);
      this.legR.pivot.rotation.set(this.limpLegXR.x, 0, this.limpLegZR.x);
    } else {
      this.legL.pivot.rotation.set(swing + this.legFlinchL.x, 0, 0);
      this.legR.pivot.rotation.set(counter + this.legFlinchR.x, 0, 0);
    }

    // --- The gun, then the arms: `poseArm` reads the hands out of the anchor's
    // matrix.
    const armed = this.weaponModel !== null;
    const holdTarget = armed && anim !== "dead" ? 1 : 0;
    this.hold += (holdTarget - this.hold) * k;
    this.reload += ((this.reloadWanted ? holdTarget : 0) - this.reload) * k;

    // The cycle runs on its own clock and restarts from the top each time the
    // blend dies, so a reload never begins halfway through the reach.
    let reach = 0;
    if (this.reload > 0.001) {
      this.reloadPhase += dt;
      const t = (this.reloadPhase % RELOAD_CYCLE_SEC) / RELOAD_CYCLE_SEC;
      reach = magReachAt(t) * this.reload;
    } else {
      this.reloadPhase = 0;
    }

    this.aimPitch +=
      (clamp(pitch, -WEAPON_PITCH_LIMIT, WEAPON_PITCH_LIMIT) * (1 - this.death) -
        this.aimPitch) *
      k;
    this.weaponAnchor.rotation.x =
      WEAPON_REST_PITCH + this.aimPitch * WEAPON_PITCH_GAIN + reach * RELOAD_RISE;
    // NEGATIVE roll: the gun is carried on the right (`weaponAnchor.position.x`
    // is +0.14), so tipping the magazine well toward the body is toward −x.
    this.weaponAnchor.rotation.z = -reach * RELOAD_ROLL;
    // The arms are solved against this matrix a few lines down, and three does
    // not compose the graph until it renders — so compose this one node now.
    this.weaponAnchor.updateMatrix();

    if (this.handSupport !== null) {
      this.handSupport.position.lerpVectors(this.gripSupport, this.gripMag, reach);
    }

    // Arms counter-swing at 70% when they are free; when there is a gun in them
    // they reach for it instead, and the slerp inside `poseArm` crosses between.
    this.poseArm(this.armL, this.handSupport, counter * 0.7, 0.07 + this.crouch * 0.05);
    this.poseArm(this.armR, this.handTrigger, swing * 0.7, -0.07 - this.crouch * 0.05);
    // On top of the solve: a dead arm lets go of the gun, a struck one jerks.
    if (falling) {
      this.limpArm(this.armL, this.limpArmXL, this.limpArmZL);
      this.limpArm(this.armR, this.limpArmXR, this.limpArmZR);
    } else if (this.flinchLive) {
      this.jerkArm(this.armL, this.armFlinchL);
      this.jerkArm(this.armR, this.armFlinchR);
    }

    // --- Head ---------------------------------------------------------------
    // Clamped hard: a network pitch of ±90° on a box neck looks broken.
    this.headPivot.rotation.x =
      clamp(pitch, -0.7, 0.7) * (1 - this.death) + this.headFlinchX.x + this.limpHeadX.x;
    this.headPivot.rotation.z = this.headFlinchZ.x + this.limpHeadZ.x;

    // --- Death --------------------------------------------------------------
    // The whole fall lives on `body`, which pivots at the feet. The root only
    // rises, so the torso lies ON the ground instead of half inside it.
    if (falling) {
      this.body.quaternion.setFromAxisAngle(this.fallAxis, this.fall.angle);
      // A man is thicker across the shoulders than from chest to back, so a
      // fall onto his side needs the extra.
      const lift =
        LIE_LIFT +
        Math.abs(this.fallDirX) * SIDE_LIFT +
        (this.pack.visible && this.fallDirZ > 0.5 ? PACK_LIFT : 0);
      this.lieLift = Math.sin(this.fall.angle) * lift;
      this.group.position.y = pos.y + this.lieLift;
      if (this.fall.settled && this.stillLimp()) this.frozen = true;
    }
  }

  hit(limb: LimbId, from: Vec3 | null): void {
    // A dying man does not flinch — he is already on his way down.
    if (this.death > 0.5 || this.fall.active) return;
    this.pushDir(from, tmpDir);

    // Rotation about +X takes +y toward +z and about +Z toward −x: the push,
    // decomposed onto the two axes the chest tilts about.
    const amp = FLINCH_AMP[limb] * FLINCH_GAIN;
    kickSpring(this.flinchX, amp * tmpDir.z);
    kickSpring(this.flinchZ, -amp * tmpDir.x);

    const head =
      (limb === "head" ? HEAD_FLINCH_HIT : HEAD_FLINCH_BODY * FLINCH_AMP[limb]) *
      FLINCH_GAIN;
    kickSpring(this.headFlinchX, head * tmpDir.z);
    kickSpring(this.headFlinchZ, -head * tmpDir.x);

    // The struck limb itself. Signed by the push but never zero — a hit from
    // the side must still move the arm it went through.
    const jerk = LIMB_FLINCH * FLINCH_GAIN * (tmpDir.z >= 0 ? 1 : -1);
    if (limb === "armL") kickSpring(this.armFlinchL, jerk);
    else if (limb === "armR") kickSpring(this.armFlinchR, jerk);
    else if (limb === "legL" || limb === "legR") {
      kickSpring(limb === "legL" ? this.legFlinchL : this.legFlinchR, jerk);
      kickSpring(this.dip, DIP_LEG * FLINCH_GAIN);
    } else if (limb === "thorax" || limb === "stomach") {
      kickSpring(this.dip, DIP_BODY * FLINCH_GAIN);
    }
    this.flinchLive = true;
  }

  kill(from: Vec3 | null, limb: LimbId | null): void {
    if (this.fall.active) return;
    this.pushDir(from, tmpDir);
    this.primeDirX = tmpDir.x;
    this.primeDirZ = tmpDir.z;
    this.primeFrom = from !== null;
    const impulse = deathImpulseFor(limb);
    this.primeStyle = impulse.style;
    this.primeAngVel = impulse.angVel;
    this.primeAt = this.clock;
    this.hasPrime = true;
  }

  /** Where a push from `from` sends the body, in the rig's own frame. */
  private pushDir(from: Vec3 | null, out: { x: number; z: number }): void {
    if (from === null) {
      // Unknown shooter: backward, the pose that reads as "shot from in front".
      out.x = 0;
      out.z = 1;
      return;
    }
    const p = this.group.position;
    fallDirLocal(from.x, from.z, p.x, p.z, this.group.rotation.y, out);
  }

  /** One frame of flinch, and the cheap exit once it has rung down. */
  private stepFlinch(dt: number): void {
    let quiet = true;
    for (const s of this.flinches) {
      stepSpring(s, dt, FLINCH_OMEGA, FLINCH_ZETA);
      if (Math.abs(s.x) > FLINCH_QUIET || Math.abs(s.v) > FLINCH_QUIET) quiet = false;
    }
    if (!quiet) return;
    for (const s of this.flinches) {
      s.x = 0;
      s.v = 0;
    }
    this.flinchLive = false;
  }

  /** The first `dead` frame: takes the primed impulse, or topples backward. */
  private startFall(): void {
    const primed = this.hasPrime && this.clock - this.primeAt <= PRIME_MAX_SEC;
    const style = primed ? this.primeStyle : "topple";
    let dirX = primed ? this.primeDirX : 0;
    let dirZ = primed ? this.primeDirZ : 1;
    // Nobody pushed him — a bleed-out folds forward over his own knees.
    if (style === "sag" && !(primed && this.primeFrom)) {
      dirX = 0;
      dirZ = -1;
    }
    this.hasPrime = false;
    this.fallDirX = dirX;
    this.fallDirZ = dirZ;
    // Rotating about (dz, 0, −dx) takes +y along (dx, 0, dz) — the push.
    this.fallAxis.set(dirZ, 0, -dirX).normalize();

    // Which way the joints bend, so none of them ends up under the body. A
    // limb hangs along −Y and its tip swings toward −Z as it bends about X, so
    // on his BACK (body −Z is up) a knee needs a POSITIVE bend; a head leads
    // with +Y and wants the opposite. On his SIDE it is the splay about Z that
    // would go through the ground instead, and the authored bends are free.
    const sideways = Math.abs(dirX) > Math.abs(dirZ);
    const bend = sideways ? 0 : dirZ >= 0 ? 1 : -1;
    aimLimp(this.limpLegXL, bend);
    aimLimp(this.limpLegXR, bend);
    aimLimp(this.limpArmXL, bend);
    aimLimp(this.limpArmXR, bend);
    aimLimp(this.limpHeadX, -bend);
    const splay = sideways ? (dirX >= 0 ? -1 : 1) : 0;
    aimLimp(this.limpLegZL, splay);
    aimLimp(this.limpLegZR, splay);
    aimLimp(this.limpArmZL, splay);
    aimLimp(this.limpArmZR, splay);
    aimLimp(this.limpHeadZ, -splay);

    this.fall.active = true;
    // Not 0: an exactly upright pendulum stands there for ever.
    this.fall.angle = 0.02;
    this.fall.bounced = false;
    this.fall.settled = false;
    this.landed = false;
    this.frozen = false;

    // The limbs start where the living pose left them, then swing into the
    // limp one with a shove, so nothing slides to its rest.
    this.seedLimp(this.limpLegXL, this.legL.pivot.rotation.x, 42);
    this.seedLimp(this.limpLegXR, this.legR.pivot.rotation.x, 44);
    this.seedLimp(this.limpLegZL, this.legL.pivot.rotation.z, 46);
    this.seedLimp(this.limpLegZR, this.legR.pivot.rotation.z, 48);
    this.seedLimp(this.limpArmXL, this.armL.pivot.rotation.x, 50);
    this.seedLimp(this.limpArmXR, this.armR.pivot.rotation.x, 52);
    this.seedLimp(this.limpArmZL, this.armL.pivot.rotation.z, 54);
    this.seedLimp(this.limpArmZR, this.armR.pivot.rotation.z, 56);
    this.seedLimp(this.limpHeadX, this.headPivot.rotation.x, 58);
    this.seedLimp(this.limpHeadZ, this.headPivot.rotation.z, 60);

    const impulse = primed ? this.primeAngVel : deathImpulseFor("thorax").angVel;
    this.fall.angVel = impulse;
    if (style === "snap") {
      // The head goes first and drags the body after it.
      const whip = 1.5 + rand01(this.seed, 41);
      kickSpring(this.limpHeadX, whip * dirZ);
      kickSpring(this.limpHeadZ, -whip * dirX);
    } else if (style === "buckle") {
      // The knees go before the topple does.
      this.fall.angVel = impulse * 0.5;
      this.buckleTarget = 1;
    } else if (style === "sag") {
      this.buckleTarget = 0.7;
    }
  }

  private seedLimp(l: Limp, from: number, salt: number): void {
    l.x = from;
    l.v = 0;
    kickSpring(l, sign01(this.seed, salt) * (0.5 + rand01(this.seed, salt + 1) * 0.5));
  }

  private stepFall(dt: number): void {
    const before = this.fall.angle;
    stepTopple(this.fall, dt);
    if (!this.landed && before < HALF_PI && this.fall.angle >= HALF_PI) {
      this.landed = true;
      // Ground impact: everything loose slaps down.
      let salt = 70;
      for (const l of this.limps) {
        const slap = LIMP_SLAP_MIN + rand01(this.seed, salt + 1) * LIMP_SLAP_SPAN;
        kickSpring(l, sign01(this.seed, salt) * slap);
        salt += 2;
      }
    }
    for (const l of this.limps) stepSpring(l, dt, LIMP_OMEGA, LIMP_ZETA, l.rest);
  }

  /** He got up again (a respawn on the same id): undo the whole ragdoll. */
  private endFall(): void {
    this.fall.active = false;
    this.fall.angle = 0;
    this.fall.angVel = 0;
    this.fall.bounced = false;
    this.fall.settled = false;
    this.landed = false;
    this.frozen = false;
    this.hasPrime = false;
    this.buckleTarget = 0;
    this.lieLift = 0;
    for (const l of this.limps) {
      l.x = 0;
      l.v = 0;
    }
    this.body.quaternion.identity();
  }

  /** Already flat, already at rest: nothing left to integrate. */
  private stillLimp(): boolean {
    if (this.flinchLive || this.death < 0.999) return false;
    if (Math.abs(this.buckle - this.buckleTarget) > LIMP_QUIET) return false;
    for (const l of this.limps) {
      if (Math.abs(l.x - l.rest) > LIMP_QUIET || Math.abs(l.v) > LIMP_QUIET) {
        return false;
      }
    }
    return true;
  }

  /** A body seen for the first time already dead: the settled pose, no fall. */
  private snapToCorpse(): void {
    this.startFall();
    this.fall.angle = HALF_PI;
    this.fall.angVel = 0;
    this.fall.bounced = true;
    this.fall.settled = true;
    this.landed = true;
    this.death = 1;
    this.hold = 0;
    this.buckle = this.buckleTarget;
    for (const l of this.limps) {
      l.x = l.rest;
      l.v = 0;
    }
  }

  /** The arm lets go and hangs, as far as `death` has got. */
  private limpArm(limb: Limb, x: Limp, z: Limp): void {
    tmpEuler.set(x.x, 0, z.x);
    tmpQuat.setFromEuler(tmpEuler);
    limb.pivot.quaternion.slerp(tmpQuat, this.death);
  }

  /** A struck arm jerks fore or aft, on top of whatever `poseArm` did. */
  private jerkArm(limb: Limb, s: Spring): void {
    if (Math.abs(s.x) < FLINCH_QUIET) return;
    tmpQuat.setFromAxisAngle(AXIS_X, s.x);
    limb.pivot.quaternion.multiply(tmpQuat);
  }

  /**
   * One arm for one frame: swinging with the gait, reaching for its hand on the
   * gun, or between the two while `hold` resolves. A one-bone solve —
   * `setFromUnitVectors` for the rotation, scale for the reach — correct here
   * because there is no elbow whose twist would need choosing. `hand` null is
   * the cheap path.
   */
  private poseArm(
    limb: Limb,
    hand: THREE.Mesh | null,
    gaitX: number,
    gaitZ: number,
  ): void {
    tmpEuler.set(gaitX, 0, gaitZ);
    const hold = hand === null ? 0 : this.hold;
    // The hand in hips space: `weaponAnchor.matrix` is the whole chain. A
    // degenerate distance falls back to the gait.
    let dist = 0;
    if (hold >= 0.001) {
      tmpVec.copy((hand as THREE.Mesh).position).applyMatrix4(this.weaponAnchor.matrix);
      tmpVec.sub(limb.pivot.position);
      dist = tmpVec.length();
    }
    if (dist < 1e-4) {
      limb.pivot.rotation.copy(tmpEuler);
      if (limb.mesh.scale.y !== 1) {
        limb.mesh.scale.y = 1;
        limb.mesh.position.y = -ARM_LEN * 0.5;
      }
      return;
    }
    tmpVec.divideScalar(dist);

    tmpQuat.setFromUnitVectors(LIMB_AXIS, tmpVec);
    tmpQuatGait.setFromEuler(tmpEuler);
    limb.pivot.quaternion.slerpQuaternions(tmpQuatGait, tmpQuat, hold);

    const s = 1 + (clamp(dist / ARM_LEN, ARM_SCALE_MIN, ARM_SCALE_MAX) - 1) * hold;
    limb.mesh.scale.y = s;
    limb.mesh.position.y = -ARM_LEN * 0.5 * s;
  }

  dispose(): void {
    // Geometries are shared and long-lived (see `geoCache`); only the
    // materials, the name-tag texture and the weapon model belong to this rig.
    this.disposeTag();
    this.disposeMarker();
    this.disposeBark();
    // Both wants cleared, so this tears the gun down and takes the light and the
    // beam off it on the way — see `syncWeaponModel`.
    this.wantWeaponId = null;
    this.wantTac = null;
    this.syncWeaponModel();
    // ...and then the two objects themselves, which are the rig's own and not
    // the gun's: `BuiltWeapon.dispose` only ever frees what it built.
    this.tacLight?.dispose();
    this.tacLight = null;
    this.tacLightTarget = null;
    this.tacBeam = null;
    this.tacBeamGeo?.dispose();
    this.tacBeamGeo = null;
    this.tacBeamMat?.dispose();
    this.tacBeamMat = null;
    this.bodyMat.dispose();
    this.gearMat.dispose();
    this.skinMat.dispose();
    this.accentMat.dispose();
    this.vestMat.dispose();
    this.helmetMat.dispose();
    this.group.clear();
  }

  private disposeTag(): void {
    if (this.tag !== null) {
      this.group.remove(this.tag);
      this.tag.material.dispose();
      this.tag = null;
    }
    if (this.tagTexture !== null) {
      this.tagTexture.dispose();
      this.tagTexture = null;
    }
  }

  private disposeBark(): void {
    if (this.bark !== null) {
      this.group.remove(this.bark);
      this.bark.material.dispose();
      this.bark = null;
    }
    if (this.barkTexture !== null) {
      this.barkTexture.dispose();
      this.barkTexture = null;
    }
  }

  /** No texture to free: the diamond is an untextured sprite (see
   *  `makeTeamMarker`), so its material is the whole of it. */
  private disposeMarker(): void {
    if (this.marker === null) return;
    this.group.remove(this.marker);
    this.marker.material.dispose();
    this.marker = null;
  }
}

export interface CharacterOptions {
  /** Entity id — drives the body tint and small per-character proportions. */
  id?: string;
  tint?: number;
  name?: string;
}

export function createCharacter(opts: CharacterOptions = {}): CharacterRig {
  // The id seeds the build variation AND the pose he will die in — see the
  // limp springs.
  const h = opts.id === undefined ? 0 : hashString(opts.id);
  const rig = new Rig(h);
  if (opts.tint !== undefined) rig.setTint(opts.tint);
  if (opts.name !== undefined) rig.setName(opts.name);
  if (opts.id !== undefined) {
    // A whisper of per-character build variation, deterministic per id.
    const s = 0.96 + rand01(h, 7) * 0.08;
    rig.group.scale.setScalar(s);
  }
  return rig;
}

// ---------------------------------------------------------------------------

/**
 * Turns every solid mesh under `root` into a shadow caster and receiver — a
 * no-op unless `?shadows=1`. Characters receive too: a player lit inside a
 * building's shadow cannot hide. Skips the tag `Sprite` and transparent
 * materials (the reticle quads).
 */
function markShadowCasters(root: THREE.Object3D): void {
  if (!SHADOWS_ENABLED) return;
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (mesh.isMesh !== true) return;
    const mat = mesh.material;
    if (!Array.isArray(mat) && mat.transparent) return;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
  });
}

interface NameSprite {
  sprite: THREE.Sprite;
  texture: THREE.CanvasTexture;
}

/**
 * Canvas-rendered name tag; `null` without a DOM. `team`/`accent` change the
 * ink only — the diamond carries the shape half of the message. The squad mint
 * outranks a role ink, which outranks the default; see `CharacterRig.setName`
 * for why that order is spelled out.
 */
function makeNameSprite(name: string, team = false, accent?: string): NameSprite | null {
  return makeTextSprite(name, team ? TEAM_CSS : (accent ?? NAME_CSS));
}

/**
 * One line of text on a dark rounded plate, as a sprite; `null` without a DOM.
 * The name tag and the bark bubble are both this — only the ink differs.
 */
function makeTextSprite(text: string, ink: string): NameSprite | null {
  if (typeof document === "undefined") return null;

  const FONT = 44;
  const PAD = 14;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (ctx === null) return null;

  ctx.font = `600 ${FONT}px system-ui, sans-serif`;
  const w = Math.ceil(ctx.measureText(text).width) + PAD * 2;
  const h = FONT + PAD * 2;
  // Power-of-two-ish sizing is irrelevant here; clamp only to stay sane.
  canvas.width = Math.min(1024, Math.max(64, w));
  canvas.height = h;

  ctx.font = `600 ${FONT}px system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "rgba(12, 16, 22, 0.55)";
  roundRect(ctx, 0, 0, canvas.width, canvas.height, 12);
  ctx.fill();
  ctx.fillStyle = ink;
  ctx.fillText(text, canvas.width * 0.5, canvas.height * 0.55);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;

  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    // Tags must stay readable through the haze.
    fog: false,
  });
  const sprite = new THREE.Sprite(material);
  sprite.userData.aspect = canvas.width / canvas.height;
  sprite.scale.set(sprite.userData.aspect * 0.5, 0.5, 1);
  sprite.renderOrder = 10;
  return { sprite, texture };
}

/**
 * The squadmate diamond: an untextured `SpriteMaterial` rotated 45° (no
 * canvas, so it exists under node). Draws THROUGH walls on purpose — the whole
 * value is knowing where the man you must not shoot is when you cannot see him.
 */
function makeTeamMarker(): THREE.Sprite {
  const material = new THREE.SpriteMaterial({
    color: TEAM_COLOR,
    transparent: true,
    // A little short of solid: a hard mint lozenge reads as UI pasted over the
    // valley, and this has to sit in it.
    opacity: 0.85,
    depthTest: false,
    depthWrite: false,
    // Fog would dim the one marker whose whole job is to survive distance.
    fog: false,
    rotation: Math.PI * 0.25,
  });
  const sprite = new THREE.Sprite(material);
  sprite.scale.setScalar(MARKER_SCALE_BASE);
  sprite.renderOrder = 11;
  return sprite;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function smoothstep01(t: number): number {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
}

/**
 * How far the support hand is onto the magazine over one `RELOAD_CYCLE_SEC` — 0
 * at both ends. Down fast, a beat seating the box, back up slower.
 */
function magReachAt(t: number): number {
  if (t < 0.35) return smoothstep01(t / 0.35);
  if (t < 0.55) return 1;
  if (t < 0.85) return 1 - smoothstep01((t - 0.55) / 0.3);
  return 0;
}
